import React from 'react';

// A single-series trend line under a stat tile. Deliberately axis-less and
// label-less: it exists to answer "which way is this going", not to be read off.
// One series, so no legend — the tile's title names it.
//
// Drawn as inline SVG rather than pulling in a chart library. A sparkline is
// twenty lines of path math, and the portal's dependency tree is worth more
// than the twenty lines.
export default function Sparkline({ points, color, width = 132, height = 34, muted = false }) {
  if (!Array.isArray(points) || points.length < 2) return null;

  const ys = points.map((p) => p.y).filter((y) => Number.isFinite(y));
  if (ys.length < 2) return null;

  const min = Math.min(...ys);
  const max = Math.max(...ys);
  // A flat series would divide by zero and, worse, render as a line pinned to
  // the top of the box — which reads as "at maximum" rather than "unchanged".
  // Centering it is the honest picture of a series that has not moved.
  const span = max - min || 1;
  const pad = 3;
  const usableH = height - pad * 2;

  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = max === min ? height / 2 : pad + usableH - ((p.y - min) / span) * usableH;
    return [x, y];
  });

  const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lastX, lastY] = coords[coords.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', opacity: muted ? 0.35 : 1, overflow: 'visible' }}
      aria-hidden="true"
      focusable="false"
    >
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* The current value gets the only marker — a trend line with a dot on
          every point is a scatter plot nobody asked for. */}
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
    </svg>
  );
}
